import React, { useMemo } from 'react';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { RTUData, IpConfigSnapshot } from '../../types/RtuData';
import { StaticCompositeTable } from './StaticCompositeTable';
import {
  compareVirtualIpEntryKeys,
  formatVirtualIpEntryLabel,
} from '../../utils/rtuStaticDisplay';

interface VirtualIpRow {
  entry: string;
  config: string;
  ip: string;
}

function deriveRows(data: RTUData | null | undefined): VirtualIpRow[] {
  const virtualIps = data?.rtuStaticData?.virtualIp?.virtualIps;
  if (!virtualIps) return [];

  return Object.entries(virtualIps)
    .sort(([left], [right]) => compareVirtualIpEntryKeys(left, right))
    .map(([key, entry]: [string, IpConfigSnapshot]) => ({
      entry:  formatVirtualIpEntryLabel(key),
      config: entry.config ?? '—',
      ip:     entry.ip     ?? '—',
    }));
}

interface Props {
  data: RTUData | null | undefined;
}

/**
 * RtuVirtualIpsCard
 *
 * Displays the virtual_ip composite block from RTU static data as a structured
 * table (Entry / Config / IP Address).  Reads the typed VirtualIpSnapshot shape
 * — no any casts required.
 *
 * Used in both the legacy-v2 static-data section and the enhanced connectivity
 * tab.
 */
export const RtuVirtualIpsCard = ({ data }: Props) => {
  const { t } = useTranslation();
  const rows = useMemo(() => deriveRows(data), [data]);

  return (
    <Card
      title={t('rtuDetail.staticData.virtualIp.title', { defaultValue: 'Virtual IPs' })}
      className="compact-card flat-card rtu-static-module-card"
    >
      <StaticCompositeTable
        rows={rows}
        columns={[
          { field: 'entry',  header: t('rtuDetail.staticData.virtualIp.colEntry',  { defaultValue: 'Entry'      }), width: '8rem' },
          {
            field: 'config',
            header: t('rtuDetail.staticData.virtualIp.colConfig', { defaultValue: 'Config' }),
            width: '6rem',
            statusTone: true,
            statusIconTooltips: {
              positive: 'rtuDetail.staticData.virtualIp.statusIcon.positive',
              negative: 'rtuDetail.staticData.virtualIp.statusIcon.negative',
            },
          },
          { field: 'ip',     header: t('rtuDetail.staticData.virtualIp.colIp',     { defaultValue: 'IP Address' }) },
        ]}
        emptyMessage={t('table.noRowsFound', { defaultValue: 'No virtual IP entries.' })}
      />
    </Card>
  );
};

export default RtuVirtualIpsCard;
